import { memo, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FastForward } from "lucide-react";
import { TopBar } from "./top-bar";
import { RetentionBanner } from "./retention-banner";
import { VipBanner } from "@/components/vip/vip-banner";
import { H3PromoBanner } from "@/components/promo/h3-promo-banner";
import { useH3Promo } from "@/hooks/use-h3-promo";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/lib/utils";

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout = memo(function MainLayout({
  children,
}: MainLayoutProps) {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { isWhale } = useUserStore();
  const { isAnnounced: h3PromoAnnounced } = useH3Promo();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className={cn("h-full flex flex-col overflow-hidden relative z-10", isWhale && "vip-theme")}>
      <RetentionBanner />
      <VipBanner />
      {h3PromoAnnounced ? (
        <H3PromoBanner />
      ) : (
        <Link
          to="/extend-video"
          className="shrink-0 z-40 flex items-center justify-center gap-2 bg-[#d4a574] text-[#1a1520] text-center text-[12px] font-semibold py-1.5 px-4 whitespace-nowrap hover:bg-[#e0b584] transition-colors"
        >
          <FastForward className="size-3.5 shrink-0" />
          New — Extend videos automatically stitch together for unlimited-length videos
        </Link>
      )}
      <div className="shrink-0 z-40">
        <TopBar />
      </div>
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-none flex flex-col"
        style={{ overscrollBehavior: "none" }}
      >
        <div className="animate-page-in flex-1">
          {children}
        </div>
        <footer className="shrink-0 border-t border-white/6 py-6 px-5 mt-auto">
          <div className="max-w-3xl mx-auto flex items-center justify-center gap-4 flex-wrap text-[11px] text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <span className="text-white/10">·</span>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <span className="text-white/10">·</span>
            <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
            <span className="text-white/10">·</span>
            <Link to="/support" className="hover:text-foreground transition-colors">Support</Link>
            <span className="text-white/10">·</span>
            <Link to="/academy" className="hover:text-foreground transition-colors">Academy</Link>
            <span className="text-white/10">·</span>
            <Link to="/affiliates" className="hover:text-foreground transition-colors">Affiliate</Link>
            <span className="text-white/10">·</span>
            <span className="text-white/20">AnimePup</span>
          </div>
        </footer>
      </main>
    </div>
  );
});
